import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useBatchTiming } from "../hooks/useBatchTiming";
import { useCourse } from "../../course/hooks/useCourse";
import { showToast } from "../../../../core/utils/alert";
import { all_routes } from "../../../router/all_routes";
import { BatchTimingPayloadType } from "../types/batchTimingRequest";
import { CourseResponseType } from "../../course/types/courseResponse";
import { useGetBatchTimingByIdQuery } from "../api/batchTimingApi";
import { useCenter } from "../../../peoples/centers/hooks/useCenter";
import { useSelector } from "react-redux";
import {
  selectCurrentUser,
  selectUserRole,
} from "../../../../core/data/slice/authSlice";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

interface FormData {
  courseId: string;
  centerId: string;
  name: string;
  startTime: string;
  endTime: string;
  status: string;
  days: string[];
}

const DAYS_OF_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const AddBatchTiming: React.FC = () => {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const { addBatchTiming, updateBatchTiming } = useBatchTiming();
  const { data: batchData, isLoading: isLoadingBatch } =
    useGetBatchTimingByIdQuery({ id: id || "" }, { skip: !id });
  const { getCourseState } = useCourse();
  const { courses, isLoading: isLoadingCourses } = getCourseState();
  const userRole = useSelector(selectUserRole);
  const currentUser = useSelector(selectCurrentUser);
  const isSuperAdmin = userRole === "superadmin";
  const { useCenterList } = useCenter({ skip: true });
  const { data: centerResponse, isLoading: isLoadingCentersFetch } = useCenterList();
  const centers = isSuperAdmin ? (centerResponse?.data?.items || []) : [];
  const isLoadingCenters = isSuperAdmin ? isLoadingCentersFetch : false;

  // Validation schema
  const validationSchema = yup.object().shape({
    courseId: yup.string().required("Course is required"),
    centerId: yup.string().required("Center is required"),
    name: yup.string().required("Batch name is required"),
    startTime: yup.string().required("Start time is required"),
    endTime: yup
      .string()
      .required("End time is required")
      .test("is-after-start", "End time must be after start time", function (value) {
        const { startTime } = this.parent;
        if (!startTime || !value) return true;
        
        // Compare times in HH:MM format
        const [startHour, startMin] = startTime.split(':').map(Number);
        const [endHour, endMin] = value.split(':').map(Number);
        
        const startMinutes = startHour * 60 + startMin;
        const endMinutes = endHour * 60 + endMin;
        
        return endMinutes > startMinutes;
      }),
    status: yup.string().required("Status is required"),
    days: yup.array().min(1, "At least one day must be selected"),
  });

  // Initialize react-hook-form
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema) as any,
    defaultValues: {
      courseId: "",
      centerId: isSuperAdmin ? "" : currentUser?.center_id?.toString() || "",
      name: "",
      startTime: "",
      endTime: "",
      status: "active",
      days: [],
    },
  });

  // Load initial data when in edit mode
  useEffect(() => {
    if (isEditMode && batchData?.data?.batch) {
      const batch = batchData.data.batch;

      // Backend stores time as HH:MM (24-hour), convert to input[type=time] value
      const formatTimeTo24h = (timeStr: string) => {
        try {
          // If already HH:MM
          if (/^\d{2}:\d{2}$/.test(timeStr)) return timeStr;

          // Parse 12-hour format e.g. "10:30am"
          const match = timeStr.match(/(\d+):(\d+)([ap]m)/i);
          if (!match) return timeStr;
          let hours = parseInt(match[1]);
          const minutes = match[2];
          const meridian = match[3].toLowerCase();
          if (meridian === "pm" && hours !== 12) hours += 12;
          if (meridian === "am" && hours === 12) hours = 0;
          return `${hours.toString().padStart(2, "0")}:${minutes}`;
        } catch {
          return timeStr;
        }
      };

      setValue("courseId", batch.course_id.toString());
      setValue("centerId", batch.center_id.toString());
      setValue("name", batch.name);
      setValue("startTime", formatTimeTo24h(batch.start_time));
      setValue("endTime", formatTimeTo24h(batch.end_time));
      setValue("status", batch.status);
      setValue("days", batch.days || []);
    }
  }, [isEditMode, batchData, setValue]);

  const watchedDays = watch("days");

  const onSubmit = async (data: FormData) => {
    const payload: BatchTimingPayloadType = {
      course_id: parseInt(data.courseId),
      center_id: isSuperAdmin
        ? parseInt(data.centerId)
        : currentUser?.center_id || 0,
      name: data.name,
      start_time: data.startTime,
      end_time: data.endTime,
      status: data.status,
      days: data.days,
    };

    try {
      if (isEditMode && id) {
        await updateBatchTiming(id, payload);
      } else {
        await addBatchTiming(payload);
      }
      navigate(all_routes.batchTimingList);
    } catch (error) {
      showToast({
        title: "Error",
        text: "Failed to save batch timing",
        icon: "error",
      });
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content content-two">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="mb-1">{isEditMode ? "Edit" : "Add"} Batch Timing</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={all_routes.batchTimingList}>Batch Timings</Link>
                </li>
                <li className="breadcrumb-item active">
                  {isEditMode ? "Edit" : "Add"} Batch Timing
                </li>
              </ol>
            </nav>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="card">
                <div className="card-header bg-light">
                  <div className="d-flex align-items-center">
                    <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                      <i className="ti ti-clock fs-16" />
                    </span>
                    <h4 className="text-dark">Batch Timing Details</h4>
                  </div>
                </div>
                <div className="card-body pb-1">
                  <div className="row">

                    {/* Course */}
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Course <span className="text-danger">*</span></label>
                        <Controller
                          name="courseId"
                          control={control}
                          render={({ field }) => (
                            <select
                              {...field}
                              className={`form-control ${errors.courseId ? "is-invalid" : ""}`}
                            >
                              <option value="">Select Course</option>
                              {courses.map((course: CourseResponseType) => (
                                <option key={course.id} value={course.id}>
                                  {course.name}
                                </option>
                              ))}
                            </select>
                          )}
                        />
                        {errors.courseId && (
                          <div className="invalid-feedback">{errors.courseId.message}</div>
                        )}
                      </div>
                    </div>

                    {/* Center — superadmin only */}
                    {isSuperAdmin && (
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">Center <span className="text-danger">*</span></label>
                          <Controller
                            name="centerId"
                            control={control}
                            render={({ field }) => (
                              <select
                                {...field}
                                className={`form-control ${errors.centerId ? "is-invalid" : ""}`}
                              >
                                <option value="">Select Center</option>
                                {centers.map((center) => (
                                  <option key={center.id} value={center.id}>
                                    {center.name}
                                  </option>
                                ))}
                              </select>
                            )}
                          />
                          {errors.centerId && (
                            <div className="invalid-feedback">{errors.centerId.message}</div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Batch Name */}
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Batch Name <span className="text-danger">*</span></label>
                        <Controller
                          name="name"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="text"
                              className={`form-control ${errors.name ? "is-invalid" : ""}`}
                              placeholder="Enter batch timing name"
                              {...field}
                            />
                          )}
                        />
                        {errors.name && (
                          <div className="invalid-feedback">{errors.name.message}</div>
                        )}
                      </div>
                    </div>

                    {/* Status */}
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Status <span className="text-danger">*</span></label>
                        <Controller
                          name="status"
                          control={control}
                          render={({ field }) => (
                            <select
                              {...field}
                              className={`form-select ${errors.status ? "is-invalid" : ""}`}
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </select>
                          )}
                        />
                        {errors.status && (
                          <div className="invalid-feedback">{errors.status.message}</div>
                        )}
                      </div>
                    </div>

                    {/* Start Time */}
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Start Time <span className="text-danger">*</span></label>
                        <Controller
                          name="startTime"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="time"
                              className={`form-control ${errors.startTime ? "is-invalid" : ""}`}
                              {...field}
                            />
                          )}
                        />
                        {errors.startTime && (
                          <div className="invalid-feedback">{errors.startTime.message}</div>
                        )}
                      </div>
                    </div>

                    {/* End Time */}
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">End Time <span className="text-danger">*</span></label>
                        <Controller
                          name="endTime"
                          control={control}
                          render={({ field }) => (
                            <input
                              type="time"
                              className={`form-control ${errors.endTime ? "is-invalid" : ""}`}
                              {...field}
                            />
                          )}
                        />
                        {errors.endTime && (
                          <div className="invalid-feedback">{errors.endTime.message}</div>
                        )}
                      </div>
                    </div>

                    {/* Days checkboxes */}
                    <div className="col-12">
                      <div className="mb-3">
                        <div className="d-flex align-items-center gap-3 mb-2">
                          <label className="form-label mb-0">Days <span className="text-danger">*</span></label>
                          <a
                            href="#"
                            className="text-primary text-decoration-none small"
                            onClick={(e) => {
                              e.preventDefault();
                              const allSelected = watchedDays?.length === DAYS_OF_WEEK.length;
                              setValue("days", allSelected ? [] : [...DAYS_OF_WEEK]);
                            }}
                          >
                            <i className="ti ti-checkbox me-1"></i>
                            {watchedDays?.length === DAYS_OF_WEEK.length ? "Deselect All" : "Select All"}
                          </a>
                        </div>
                        <div className="border rounded p-3 bg-light">
                          <div className="d-flex flex-wrap gap-3">
                            {DAYS_OF_WEEK.map((day) => (
                              <div key={day} className="form-check">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id={`day-${day}`}
                                  checked={watchedDays?.includes(day)}
                                  onChange={() => {
                                    const newDays = watchedDays?.includes(day)
                                      ? watchedDays.filter((d: string) => d !== day)
                                      : [...(watchedDays || []), day];
                                    setValue("days", newDays);
                                  }}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor={`day-${day}`}
                                >
                                  {day}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                        {errors.days && (
                          <div className="invalid-feedback d-block">{errors.days.message}</div>
                        )}
                      </div>
                    </div>

                  </div>
                </div>
              </div>
              <div className="text-end mt-4">
                <Link
                  to={all_routes.batchTimingList}
                  className="btn btn-light me-3"
                >
                  Cancel
                </Link>
                <button type="submit" className="btn btn-primary">
                  {isEditMode ? "Update" : "Add"} Batch Timing
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddBatchTiming;
